import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { analyzeFood } from '../services/foodService';
import { toggleFavorite } from '../services/foodService';
import ImageUpload from '../components/ImageUpload/ImageUpload';
import './AnalyzePage.css';

const AnalyzePage = () => {
  const { tokens } = useAuth();
  const { t } = useLanguage();
  const [imageBase64, setImageBase64] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [portion, setPortion] = useState(100); // Default 100g
  const [manualFoodName, setManualFoodName] = useState(''); // Manual food name input
  const [useManualName, setUseManualName] = useState(false); // Toggle manual input

  const handleImageSelect = (base64, file) => {
    setImageBase64(base64);
    setImagePreview(URL.createObjectURL(file));
    setError(null);
    setResult(null);
    setManualFoodName('');
    setUseManualName(false);
  };

  const handleAnalyze = async () => {
    if (!imageBase64) return;

    // If user wants to use manual name, skip vision and go straight to LLM
    if (useManualName && manualFoodName.trim()) {
      await analyzeWithManualName(manualFoodName.trim());
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await analyzeFood(tokens.access_token, imageBase64, 'uz');
      setResult(data);
      // Set portion to estimated weight from backend
      if (data.consumed_amount) {
        setPortion(data.consumed_amount);
      }
    } catch (err) {
      console.error('Analysis error:', err);
      // Extract error message from response
      let errorMessage = err.message || t('error_analyzing');

      // Check if it's a validation error
      if (err.response?.status === 400 || err.message?.includes('400')) {
        errorMessage = 'Rasm formati yoki hajmi noto\'g\'ri. Iltimos, boshqa rasm tanlang (max 15MB, JPEG/PNG/WebP).';
      } else if (err.response?.status === 429 || err.message?.includes('429') || err.message?.includes('limit')) {
        errorMessage = 'Kunlik limit tugadi! Ertaga yana foydalaning.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const analyzeWithManualName = async (foodName) => {
    setLoading(true);
    setError(null);

    try {
      // Call backend API directly with manual food name (skip vision)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/food/analyze-by-name`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          food_name: foodName,
          image_base64: imageBase64,
          language: 'uz'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Better error handling for common cases
        if (response.status === 400) {
          throw new Error('Rasm formati yoki hajmi noto\'g\'ri. Iltimos, boshqa rasm tanlang.');
        } else if (response.status === 429) {
          throw new Error('Kunlik limit tugadi! Ertaga yana foydalaning.');
        } else {
          throw new Error(errorData.detail?.message || errorData.detail || 'Tahlil qilishda xato');
        }
      }

      const data = await response.json();
      setResult(data);
      if (data.consumed_amount) {
        setPortion(data.consumed_amount);
      }
    } catch (err) {
      console.error('Manual analysis error:', err);
      setError(err.message || t('error_analyzing'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setImageBase64(null);
    setImagePreview(null);
    setResult(null);
    setError(null);
    setManualFoodName('');
    setUseManualName(false);
  };

  const handleLogMeal = async () => {
    if (!result?.id) {
      alert(t('error_occurred') || 'Xatolik yuz berdi');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/food/history/${result.id}/log-meal?consumed_amount=${portion}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to log meal');
      }

      alert(t('meal_logged') || 'Ovqat kunlik hisobga qo\'shildi!');
    } catch (error) {
      console.error('Log meal error:', error);
      alert(t('error_occurred') || 'Xatolik yuz berdi');
    }
  };

  const handleAddToFavorites = async () => {
    if (!result?.id) {
      alert(t('error_occurred') || 'Xatolik yuz berdi');
      return;
    }

    try {
      await toggleFavorite(tokens.access_token, result.id, true);
      alert(t('added_to_favorites') || 'Sevimlilarga qo\'shildi!');
    } catch (error) {
      console.error('Favorite error:', error);
      alert(t('error_occurred') || 'Xatolik yuz berdi');
    }
  };

  const calculateNutrition = (baseValue) => {
    return ((baseValue * portion) / 100).toFixed(1);
  };

  return (
    <div className="analyze-page">
      <div className="analyze-header">
        <h1>{t('analyze_food')}</h1>
        <p>{t('analyze_subtitle')}</p>
      </div>

      {!result && (
        <div className="analyze-upload-section">
          <ImageUpload onImageSelect={handleImageSelect} disabled={loading} />

          {imageBase64 && !loading && (
            <>
              {/* Optional manual food name input */}
              <div className="manual-name-section">
                <label className="manual-name-toggle">
                  <input
                    type="checkbox"
                    checked={useManualName}
                    onChange={(e) => setUseManualName(e.target.checked)}
                  />
                  <span>{t('enter_food_name_manually') || 'Taom nomini o\'zim kiritaman'}</span>
                </label>

                {useManualName && (
                  <div className="manual-name-input-wrapper">
                    <input
                      type="text"
                      value={manualFoodName}
                      onChange={(e) => setManualFoodName(e.target.value)}
                      placeholder={t('food_name_placeholder') || 'Masalan: Osh, Lag\'mon, Manti...'}
                      className="manual-name-input"
                    />
                    <small className="manual-name-hint">
                      {t('manual_name_hint') || 'Taom nomini kiritish AIni tezlashtiradi va aniqlashtiradi'}
                    </small>
                  </div>
                )}
              </div>

              <button
                className="analyze-button"
                onClick={handleAnalyze}
                disabled={useManualName && !manualFoodName.trim()}
              >
                <span className="icon">🔍</span>
                {t('analyze')}
              </button>
            </>
          )}

          {loading && (
            <div className="analyzing-state">
              <div className="spinner"></div>
              <p>{t('analyzing')}...</p>
              <small>{t('this_may_take_moment')}</small>
            </div>
          )}

          {error && (
            <div className="error-message">
              <span className="icon">⚠️</span>
              <p>{error}</p>
              <button onClick={handleReset} className="retry-button">
                {t('try_again')}
              </button>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="analyze-results">
          <div className="results-header">
            <h2>✅ {t('analysis_complete')}</h2>
            <button onClick={handleReset} className="new-analysis-button">
              {t('new_analysis')}
            </button>
          </div>

          <div className="result-sections">
            {/* Food Name */}
            <div className="result-card">
              <h3>🍽️ {t('food_name')}</h3>
              <p className="food-name">{result.food_name || t('unknown_food')}</p>
            </div>

            {/* Nutrition Information */}
            <div className="result-card nutrition-card">
              <h3>📊 {t('nutrition_info')}</h3>

              {/* Portion Size Selector */}
              <div className="portion-selector">
                <label>{t('portion_size')}: </label>
                <input
                  type="number"
                  value={portion}
                  onChange={(e) => setPortion(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                  max="1000"
                  className="portion-input"
                />
                <span className="portion-unit">g</span>
              </div>

              <div className="nutrition-grid">
                <div className="nutrition-item">
                  <span className="label">{t('calories')}</span>
                  <span className="value">{calculateNutrition(result.nutrition?.calories || 0)} kcal</span>
                </div>
                <div className="nutrition-item">
                  <span className="label">{t('protein')}</span>
                  <span className="value">{calculateNutrition(result.nutrition?.protein || 0)}g</span>
                </div>
                <div className="nutrition-item">
                  <span className="label">{t('carbs')}</span>
                  <span className="value">{calculateNutrition(result.nutrition?.carbs || 0)}g</span>
                </div>
                <div className="nutrition-item">
                  <span className="label">{t('fat')}</span>
                  <span className="value">{calculateNutrition(result.nutrition?.fat || 0)}g</span>
                </div>
              </div>
            </div>

            {/* Ingredients */}
            {result.ingredients && result.ingredients.length > 0 && (
              <div className="result-card">
                <h3>🥗 {t('ingredients')}</h3>
                <ul className="ingredients-list">
                  {result.ingredients.map((ingredient, index) => (
                    <li key={index}>{ingredient}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recipe */}
            {result.recipe && (
              <div className="result-card">
                <h3>👨‍🍳 {t('recipe')}</h3>
                <p className="recipe-text">{result.recipe}</p>
              </div>
            )}

            {/* Cooking Time */}
            {result.cooking_time && (
              <div className="result-card">
                <h3>⏱️ {t('cooking_time')}</h3>
                <p className="cooking-time-text">{result.cooking_time} {t('minutes')}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="result-actions">
            <button className="action-button save-button" onClick={handleLogMeal}>
              <span className="icon">📝</span>
              {t('log_meal')}
            </button>
            <button className="action-button favorite-button" onClick={handleAddToFavorites}>
              <span className="icon">⭐</span>
              {t('add_to_favorites')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyzePage;
